/**
 * ProfileSetupGate — shown when a user tries to upload without a complete profile.
 * "Complete profile" = has username (userProfiles) + has avatar (userAvatars).
 *
 * When both are satisfied the gate auto-closes and the upload flow opens.
 */

import React, { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, User, Pencil, Check, Loader2, Camera, Upload, ArrowRight } from 'lucide-react';
import Cropper from 'react-easy-crop';
import type { Area, Point } from 'react-easy-crop';
import { toast } from 'sonner';
import { notify } from '@/utils/notifications';

import { useRealtimeData } from '@/hooks/use-realtime-data';
import { subscribeUserProfiles, UserProfilesResponse } from '@/lib/collections/userProfiles';
import { subscribeUserAvatars, UserAvatarsResponse } from '@/lib/collections/userAvatars';
import { setUserProfiles } from '@/lib/collections/userProfiles';
import { setUserAvatars } from '@/lib/collections/userAvatars';
import { setUsernames, deleteUsernames } from '@/lib/collections/usernames';
import type { UsernamesResponse } from '@/lib/collections/usernames';
import { get, setFile, getFiles, Time, Address, FileItem } from '@/lib/db-client';
import { ADMIN_ADDRESS } from '@/lib/constants';
import { containsProfanity, getProfanityError } from '@/utils/profanity-blacklist';

const USERNAME_REGEX = /^[a-zA-Z0-9_]{4,15}$/;

const RESERVED_USERNAMES = new Set([
  // System words
  'username', 'usernames', 'admin', 'administration', 'administrator', 'moderator', 'mod', 'staff', 'support', 'help',
  'system', 'official', 'seekscape', 'seeker', 'root', 'superuser', 'owner',
  'operator', 'bot', 'robot', 'automated',
  // Crypto brands
  'bitcoin', 'ethereum', 'solana', 'phantom', 'metamask', 'binance', 'coinbase',
  'kraken', 'opensea', 'magiceden', 'jupiter', 'raydium', 'orca', 'drift', 'tensor',
  // Social/brands
  'instagram', 'twitter', 'tiktok', 'youtube', 'facebook', 'apple', 'google',
  'microsoft', 'amazon', 'netflix', 'spotify', 'telegram', 'whatsapp', 'discord',
]);

async function getCroppedBlob(imageSrc: string, pixelCrop: Area): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      const OUTPUT_SIZE = 512;
      const canvas = document.createElement('canvas');
      canvas.width = OUTPUT_SIZE;
      canvas.height = OUTPUT_SIZE;
      const ctx = canvas.getContext('2d');
      if (!ctx) { reject(new Error('Canvas context unavailable')); return; }
      ctx.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
      canvas.toBlob(
        (blob) => { if (blob) resolve(blob); else reject(new Error('Canvas toBlob failed')); },
        'image/jpeg', 0.85,
      );
    };
    image.onerror = () => reject(new Error('Image load failed'));
    image.src = imageSrc;
  });
}

interface ProfileSetupGateProps {
  isOpen: boolean;
  walletAddress: string;
  /** Called when the profile is complete — caller should open the upload modal */
  onProfileComplete: () => void;
  onClose: () => void;
}

type Step = 'intro' | 'avatar' | 'username' | 'done';

export const ProfileSetupGate: React.FC<ProfileSetupGateProps> = ({
  isOpen,
  walletAddress,
  onProfileComplete,
  onClose,
}) => {
  // ── Realtime profile data ───────────────────────────────────────────────────
  const { data: profileData } = useRealtimeData<UserProfilesResponse | null>(
    subscribeUserProfiles,
    isOpen && !!walletAddress,
    walletAddress,
  );

  const { data: avatarData } = useRealtimeData<UserAvatarsResponse | null>(
    subscribeUserAvatars,
    isOpen && !!walletAddress,
    walletAddress,
  );

  const hasUsername = !!(profileData?.username);
  const hasAvatar = !!(avatarData?.avatarUrl);
  const isComplete = hasUsername && hasAvatar;

  // ── Step state ──────────────────────────────────────────────────────────────
  const [step, setStep] = useState<Step>('intro');

  // ── Avatar upload state ─────────────────────────────────────────────────────
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [savingAvatar, setSavingAvatar] = useState(false);
  // Holds the uploaded avatar URL in local state until final submit.
  // NOT persisted to the profile collection until the user completes both steps.
  const [pendingAvatarUrl, setPendingAvatarUrl] = useState<string | null>(null);

  // ── Username state ──────────────────────────────────────────────────────────
  const [usernameInput, setUsernameInput] = useState('');
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [usernameAvailability, setUsernameAvailability] = useState<'idle' | 'checking' | 'available' | 'taken' | 'error'>('idle');
  const [usernameReserved, setUsernameReserved] = useState<false | 'reserved' | 'profanity'>(false);
  const [savingUsername, setSavingUsername] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onCropComplete = useCallback((_: Area, pixels: Area) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') setCropImageSrc(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSaveAvatar = async () => {
    if (!croppedAreaPixels || !cropImageSrc) return;
    setSavingAvatar(true);
    try {
      const blob = await getCroppedBlob(cropImageSrc, croppedAreaPixels);
      const filename = `avatar-${walletAddress}-${Date.now()}.jpg`;
      const file = new File([blob], filename, { type: 'image/jpeg' });
      const uploadPath = `appFiles/${filename}`;

      const uploaded = await setFile(uploadPath, file);
      if (!uploaded) { notify.error('Upload failed. Please try again.', walletAddress); return; }

      const filesResult = await getFiles(uploadPath) as FileItem[] | null;
      const fileRecord = Array.isArray(filesResult) ? filesResult[0] : null;
      const avatarUrl = fileRecord?.url ?? '';
      if (!avatarUrl) { notify.error('Upload succeeded but URL was missing.', walletAddress); return; }

      // Store URL in local state only — do NOT persist to the profile collection yet.
      // The avatar will be saved to userAvatars atomically with the username on final submit.
      setPendingAvatarUrl(avatarUrl);
      setCropImageSrc(null);
      setStep('username');
    } catch (err) {
      notify.error(err instanceof Error ? err.message : 'Something went wrong uploading your avatar.', walletAddress);
    } finally {
      setSavingAvatar(false);
    }
  };

  const validateUsername = (value: string): string | null => {
    if (value.length === 0) return null;
    if (value.length < 4) return 'At least 4 characters required';
    if (value.length > 15) return 'Maximum 15 characters';
    if (!USERNAME_REGEX.test(value)) return 'Letters, numbers, and underscores only';
    return null;
  };

  const checkAvailability = useCallback(async (value: string) => {
    const lower = value.trim().toLowerCase();

    // Reserved username check — bypassed only for the platform admin
    if (walletAddress !== ADMIN_ADDRESS) {
      if (containsProfanity(value)) {
        setUsernameReserved('profanity');
        setUsernameAvailability('taken');
        return;
      }
      if (RESERVED_USERNAMES.has(lower) || lower.includes('seekscape')) {
        setUsernameReserved('reserved');
        setUsernameAvailability('taken');
        return;
      }
    }

    setUsernameReserved(false);
    setUsernameAvailability('checking');
    let existing: UsernamesResponse | null;
    try {
      existing = await get(`usernames/${lower}`) as UsernamesResponse | null;
    } catch {
      setUsernameAvailability('error');
      return;
    }
    console.log('[ProfileSetupGate] availability check:', value.toLowerCase(), 'existing:', existing, 'walletAddress:', walletAddress);
    if (!existing) {
      setUsernameAvailability('available');
    } else if (existing.owner === walletAddress) {
      setUsernameAvailability('available');
    } else {
      setUsernameAvailability('taken');
    }
  }, [walletAddress]);

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setUsernameInput(value);
    setUsernameAvailability('idle');
    setUsernameReserved(false);
    const err = validateUsername(value);
    setUsernameError(err);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!err && value.length >= 4) {
      debounceRef.current = setTimeout(() => checkAvailability(value), 300);
    }
  };

  const handleSaveUsername = async () => {
    if (savingUsername) return;
    const trimmed = usernameInput.trim();
    const err = validateUsername(trimmed);
    if (err || usernameAvailability !== 'available') return;

    // Determine which avatar URL to persist: prefer newly uploaded pending URL,
    // fall back to the already-saved avatar (user may be re-doing only username).
    const avatarUrlToSave = pendingAvatarUrl ?? avatarData?.avatarUrl ?? '';
    if (!avatarUrlToSave) {
      notify.error('Please upload an avatar photo first.', walletAddress);
      setStep('avatar');
      return;
    }

    setSavingUsername(true);
    try {
      const lower = trimmed.toLowerCase();

      // Delete old username if they had one
      const currentUsername = profileData?.username;
      if (currentUsername && currentUsername.toLowerCase() !== lower) {
        await deleteUsernames(currentUsername.toLowerCase());
      }

      const claimed = await setUsernames(lower, {
        username: trimmed,
        owner: Address.publicKey(walletAddress),
        claimedAt: Time.Now,
      });

      if (!claimed) {
        notify.error('Username already taken. Try another.', walletAddress);
        setUsernameAvailability('taken');
        return;
      }

      // Persist the avatar atomically with the username save.
      // If pendingAvatarUrl is set, this is the first time it's written to the profile.
      // If it's already saved (user only updated username), we still write to confirm.
      const avatarSaved = await setUserAvatars(walletAddress, { avatarUrl: avatarUrlToSave, updatedAt: Time.Now });
      if (!avatarSaved) {
        await deleteUsernames(lower);
        notify.error('Failed to save avatar. Please try again.', walletAddress);
        return;
      }

      const profileSaved = await setUserProfiles(walletAddress, {
        username: trimmed,
        lastUsernameChangeAt: Time.Now,
      });

      if (!profileSaved) {
        await deleteUsernames(lower);
        notify.error('Failed to save profile. Please try again.', walletAddress);
        return;
      }

      setPendingAvatarUrl(null);
    } catch (err) {
      notify.error('Something went wrong. Please try again.', walletAddress);
    } finally {
      setSavingUsername(false);
    }
  };

  // When both are complete, trigger the callback
  React.useEffect(() => {
    if (isOpen && isComplete && step !== 'intro') {
      setStep('done');
    }
  }, [isOpen, isComplete, step]);

  const handleProceed = () => {
    if (isComplete) {
      onProfileComplete();
      onClose();
    }
  };

  const handleClose = () => {
    setStep('intro');
    setCropImageSrc(null);
    setPendingAvatarUrl(null);
    setUsernameInput('');
    setUsernameError(null);
    setUsernameAvailability('idle');
    setUsernameReserved(false);
    onClose();
  };

  const accentColor = '#a78bfa';
  const rgb = '167,139,250';

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          style={{
            position: 'fixed', inset: 0, zIndex: 5000,
            background: 'rgba(0,0,0,0.82)',
            backdropFilter: 'blur(16px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 16,
          }}
          onClick={handleClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 16 }}
            transition={{ type: 'spring', stiffness: 380, damping: 30 }}
            onClick={e => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: 420,
              background: '#0c0b18',
              borderRadius: 22,
              border: `1px solid rgba(${rgb},0.18)`,
              boxShadow: `0 24px 60px rgba(0,0,0,0.7), 0 0 0 1px rgba(${rgb},0.06)`,
              overflow: 'hidden',
              maxHeight: 'calc(100vh - 32px)',
              overflowY: 'auto',
            }}
          >
            {/* Header */}
            <div style={{
              padding: '18px 20px 0',
              display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
            }}>
              <div>
                <h3 style={{
                  fontSize: 18, fontWeight: 800, color: '#f0f0f5', margin: 0,
                  fontFamily: "'Syne', sans-serif", letterSpacing: '-0.02em',
                }}>
                  Set up your profile
                </h3>
                <p style={{
                  fontSize: 12, color: `rgba(${rgb},0.6)`, margin: '3px 0 0',
                  fontFamily: "'DM Mono', monospace",
                }}>
                  Required to upload content
                </p>
              </div>
              <motion.button
                whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                onClick={handleClose}
                style={{
                  width: 30, height: 30, borderRadius: 8,
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', color: 'rgba(240,240,245,0.5)',
                  flexShrink: 0,
                }}
              >
                <X size={14} />
              </motion.button>
            </div>

            <div style={{ padding: '16px 20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>

              {/* Intro explanation */}
              {step === 'intro' && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
                >
                  <p style={{
                    fontSize: 13, color: 'rgba(240,240,245,0.6)', margin: 0,
                    fontFamily: "'Syne', sans-serif", lineHeight: 1.6,
                  }}>
                    To upload content to Seekscape, you need a public profile with a username and avatar. This lets the community know who created the content.
                  </p>

                  {/* Step checklist */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {[
                      { label: 'Upload an avatar photo', done: hasAvatar },
                      { label: 'Choose a username', done: hasUsername },
                    ].map(({ label, done }) => (
                      <div key={label} style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '10px 14px',
                        borderRadius: 10,
                        background: done ? 'rgba(34,197,94,0.06)' : 'rgba(255,255,255,0.04)',
                        border: done ? '1px solid rgba(34,197,94,0.25)' : '1px solid rgba(255,255,255,0.08)',
                      }}>
                        <div style={{
                          width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                          background: done ? 'rgba(34,197,94,0.18)' : `rgba(${rgb},0.12)`,
                          border: done ? '1px solid rgba(34,197,94,0.4)' : `1px solid rgba(${rgb},0.3)`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          {done
                            ? <Check size={12} color="#22c55e" strokeWidth={2.5} />
                            : <div style={{ width: 7, height: 7, borderRadius: '50%', background: `rgba(${rgb},0.5)` }} />
                          }
                        </div>
                        <span style={{
                          fontSize: 13, fontWeight: 600,
                          color: done ? 'rgba(240,240,245,0.5)' : '#f0f0f5',
                          fontFamily: "'Syne', sans-serif",
                          textDecoration: done ? 'line-through' : 'none',
                        }}>
                          {label}
                        </span>
                        {done && (
                          <span style={{
                            marginLeft: 'auto', fontSize: 10, fontWeight: 700,
                            color: '#22c55e', fontFamily: "'Syne', sans-serif",
                            textTransform: 'uppercase', letterSpacing: '0.06em',
                          }}>
                            Done
                          </span>
                        )}
                      </div>
                    ))}
                  </div>

                  {isComplete ? (
                    <motion.button
                      whileHover={{ scale: 1.02, boxShadow: `0 0 20px rgba(${rgb},0.4)` }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleProceed}
                      style={{
                        width: '100%', padding: '13px 16px', borderRadius: 12,
                        background: `rgba(${rgb},0.85)`,
                        border: `1px solid rgba(${rgb},0.45)`,
                        color: '#fff', fontSize: 14, fontWeight: 700,
                        cursor: 'pointer', fontFamily: "'Syne', sans-serif",
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      }}
                    >
                      <Upload size={15} />
                      Continue to Upload
                    </motion.button>
                  ) : (
                    <motion.button
                      whileHover={{ scale: 1.02, boxShadow: `0 0 20px rgba(${rgb},0.3)` }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setStep((!hasAvatar && !pendingAvatarUrl) ? 'avatar' : 'username')}
                      style={{
                        width: '100%', padding: '13px 16px', borderRadius: 12,
                        background: `rgba(${rgb},0.15)`,
                        border: `1px solid rgba(${rgb},0.35)`,
                        color: accentColor, fontSize: 14, fontWeight: 700,
                        cursor: 'pointer', fontFamily: "'Syne', sans-serif",
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      }}
                    >
                      Get Started
                      <ArrowRight size={15} />
                    </motion.button>
                  )}
                </motion.div>
              )}

              {/* Avatar step */}
              {step === 'avatar' && (
                <motion.div
                  initial={{ opacity: 0, x: 12 }}
                  animate={{ opacity: 1, x: 0 }}
                  style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Camera size={15} color={accentColor} />
                    <span style={{
                      fontSize: 11, fontWeight: 700, color: accentColor,
                      fontFamily: "'Syne', sans-serif", textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                    }}>
                      Step 1 of 2 — Avatar
                    </span>
                  </div>

                  {/* Hidden file input */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleFileSelect}
                  />

                  {cropImageSrc ? (
                    /* Crop view */
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      <div style={{ position: 'relative', width: '100%', height: 260, background: '#0a0a0f', borderRadius: 12, overflow: 'hidden' }}>
                        <Cropper
                          image={cropImageSrc}
                          crop={crop}
                          zoom={zoom}
                          aspect={1}
                          cropShape="round"
                          showGrid={false}
                          onCropChange={setCrop}
                          onZoomChange={setZoom}
                          onCropComplete={onCropComplete}
                          style={{
                            containerStyle: { background: '#0a0a0f' },
                            cropAreaStyle: { border: `2px solid rgba(${rgb},0.8)`, boxShadow: '0 0 0 9999px rgba(0,0,0,0.6)' },
                          }}
                        />
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 11, color: 'rgba(240,240,245,0.4)', fontFamily: "'DM Mono', monospace", flexShrink: 0 }}>Zoom</span>
                        <input
                          type="range" min={1} max={3} step={0.01} value={zoom}
                          onChange={e => setZoom(Number(e.target.value))}
                          style={{ flex: 1, accentColor, cursor: 'pointer' }}
                        />
                      </div>

                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          onClick={() => setCropImageSrc(null)}
                          style={{
                            flex: 1, height: 42, borderRadius: 10,
                            background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                            color: 'rgba(240,240,245,0.7)', fontSize: 13, fontWeight: 600,
                            fontFamily: "'Syne', sans-serif", cursor: 'pointer',
                          }}
                        >
                          Back
                        </button>
                        <motion.button
                          whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}
                          onClick={handleSaveAvatar}
                          disabled={savingAvatar}
                          style={{
                            flex: 2, height: 42, borderRadius: 10,
                            background: savingAvatar ? `rgba(${rgb},0.35)` : `rgba(${rgb},0.85)`,
                            border: `1px solid rgba(${rgb},0.45)`,
                            color: '#fff', fontSize: 13, fontWeight: 700,
                            fontFamily: "'Syne', sans-serif", cursor: savingAvatar ? 'not-allowed' : 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                          }}
                        >
                          {savingAvatar ? <><Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> Saving…</> : 'Save Avatar'}
                        </motion.button>
                      </div>
                    </div>
                  ) : (
                    /* Pick photo view */
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {/* Current avatar preview — shows pending upload first, then saved avatar */}
                      <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <div style={{
                          width: 88, height: 88, borderRadius: '50%',
                          background: (pendingAvatarUrl || avatarData?.avatarUrl) ? 'transparent' : `rgba(${rgb},0.1)`,
                          border: `1.5px solid rgba(${rgb},0.35)`,
                          overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          {(pendingAvatarUrl || avatarData?.avatarUrl)
                            ? <img src={pendingAvatarUrl ?? avatarData?.avatarUrl} alt="Avatar preview" loading="lazy" decoding="async" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            : <User size={36} color={accentColor} strokeWidth={1.6} />
                          }
                        </div>
                      </div>

                      {pendingAvatarUrl && (
                        <p style={{
                          textAlign: 'center', fontSize: 12,
                          color: `rgba(${rgb},0.8)`, margin: 0,
                          fontFamily: "'Syne', sans-serif",
                        }}>
                          Avatar selected — it will be saved when you complete setup
                        </p>
                      )}
                      {!pendingAvatarUrl && hasAvatar && (
                        <p style={{
                          textAlign: 'center', fontSize: 12,
                          color: 'rgba(34,197,94,0.8)', margin: 0,
                          fontFamily: "'Syne', sans-serif",
                        }}>
                          Avatar already set — you can update it or continue
                        </p>
                      )}

                      <motion.button
                        whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}
                        onClick={() => fileInputRef.current?.click()}
                        style={{
                          width: '100%', padding: '12px 14px',
                          borderRadius: 10,
                          background: 'rgba(255,255,255,0.04)',
                          border: '1px dashed rgba(255,255,255,0.2)',
                          color: 'rgba(240,240,245,0.6)',
                          cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                          fontFamily: "'Syne', sans-serif", fontSize: 13, fontWeight: 600,
                        }}
                      >
                        <Camera size={15} />
                        {hasAvatar ? 'Change avatar photo' : 'Choose avatar photo'}
                      </motion.button>

                      {(pendingAvatarUrl || hasAvatar) && (
                        <motion.button
                          whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}
                          onClick={() => setStep('username')}
                          style={{
                            width: '100%', padding: '12px 14px', borderRadius: 10,
                            background: `rgba(${rgb},0.12)`,
                            border: `1px solid rgba(${rgb},0.28)`,
                            color: accentColor, fontSize: 13, fontWeight: 700,
                            cursor: 'pointer', fontFamily: "'Syne', sans-serif",
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                          }}
                        >
                          Continue <ArrowRight size={13} />
                        </motion.button>
                      )}
                    </div>
                  )}
                </motion.div>
              )}

              {/* Username step */}
              {step === 'username' && (
                <motion.div
                  initial={{ opacity: 0, x: 12 }}
                  animate={{ opacity: 1, x: 0 }}
                  style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Pencil size={13} color={accentColor} />
                    <span style={{
                      fontSize: 11, fontWeight: 700, color: accentColor,
                      fontFamily: "'Syne', sans-serif", textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                    }}>
                      Step 2 of 2 — Username
                    </span>
                  </div>

                  {hasUsername && (
                    <p style={{
                      fontSize: 12, color: 'rgba(34,197,94,0.8)', margin: 0,
                      fontFamily: "'Syne', sans-serif',",
                    }}>
                      Current username: @{profileData?.username}
                    </p>
                  )}

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {/* @ prefix + input */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{
                        fontSize: 16, fontWeight: 700, color: accentColor,
                        fontFamily: "'Syne', sans-serif", flexShrink: 0, userSelect: 'none',
                      }}>@</span>
                      <input
                        type="text"
                        value={usernameInput}
                        onChange={handleUsernameChange}
                        onKeyDown={e => {
                          if (e.key === 'Enter' && !savingUsername && usernameAvailability === 'available' && !usernameError) handleSaveUsername();
                        }}
                        placeholder="username"
                        maxLength={15}
                        autoComplete="off"
                        autoCapitalize="none"
                        spellCheck={false}
                        style={{
                          flex: 1,
                          background: 'rgba(255,255,255,0.06)',
                          border: `1px solid ${
                            usernameError || usernameAvailability === 'taken'
                              ? 'rgba(239,68,68,0.6)'
                              : usernameAvailability === 'available'
                                ? 'rgba(34,197,94,0.5)'
                                : 'rgba(255,255,255,0.12)'
                          }`,
                          borderRadius: 8,
                          padding: '9px 12px',
                          color: '#f0f0f5',
                          fontSize: 14, fontFamily: "'DM Mono', monospace",
                          outline: 'none',
                          transition: 'border-color 0.18s ease',
                        }}
                      />
                      <div style={{ width: 20, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
                        {usernameAvailability === 'checking' && <Loader2 size={14} color="rgba(240,240,245,0.4)" style={{ animation: 'spin 1s linear infinite' }} />}
                        {usernameAvailability === 'available' && !usernameError && <Check size={14} color="#22c55e" strokeWidth={2.5} />}
                        {(usernameAvailability === 'taken' || usernameAvailability === 'error' || (usernameError && usernameInput.length > 0)) && <X size={14} color="#ef4444" strokeWidth={2.5} />}
                      </div>
                    </div>

                    {/* Status */}
                    {(usernameError || usernameAvailability === 'taken' || usernameAvailability === 'available' || usernameAvailability === 'error') && (
                      <div style={{
                        fontSize: 11, fontFamily: "'Syne', sans-serif",
                        color: (usernameError || usernameAvailability === 'taken' || usernameAvailability === 'error') ? '#ef4444' : '#22c55e',
                      }}>
                        {usernameError
                          ? usernameError
                          : usernameAvailability === 'taken'
                            ? (usernameReserved === 'profanity'
                                ? 'Никнейм содержит запрещённые слова'
                                : usernameReserved === 'reserved'
                                  ? 'This username is not available'
                                  : 'This username is taken')
                            : usernameAvailability === 'error'
                              ? "Couldn't check availability — try again"
                              : 'Available!'}
                      </div>
                    )}

                    {!usernameError && usernameAvailability === 'idle' && (
                      <div style={{ fontSize: 10, color: 'rgba(240,240,245,0.25)', fontFamily: "'Syne', sans-serif" }}>
                        4–15 chars · letters, numbers, underscores
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: 8 }}>
                    {!hasAvatar && (
                      <button
                        onClick={() => setStep('avatar')}
                        style={{
                          flex: 1, height: 42, borderRadius: 10,
                          background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                          color: 'rgba(240,240,245,0.7)', fontSize: 13, fontWeight: 600,
                          fontFamily: "'Syne', sans-serif", cursor: 'pointer',
                        }}
                      >
                        Back
                      </button>
                    )}
                    <motion.button
                      whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}
                      onClick={handleSaveUsername}
                      disabled={savingUsername || usernameAvailability !== 'available' || !!usernameError || usernameInput.length < 4}
                      style={{
                        flex: 2, height: 42, borderRadius: 10,
                        background: (savingUsername || usernameAvailability !== 'available' || !!usernameError || usernameInput.length < 4)
                          ? 'rgba(255,255,255,0.06)'
                          : `rgba(${rgb},0.85)`,
                        border: `1px solid rgba(${rgb},0.3)`,
                        color: (usernameAvailability === 'available' && !usernameError && usernameInput.length >= 4) ? '#fff' : 'rgba(240,240,245,0.25)',
                        fontSize: 13, fontWeight: 700,
                        fontFamily: "'Syne', sans-serif",
                        cursor: (savingUsername || usernameAvailability !== 'available' || !!usernameError || usernameInput.length < 4) ? 'not-allowed' : 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                        transition: 'all 0.15s ease',
                      }}
                    >
                      {savingUsername ? <><Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> Saving…</> : 'Save Username'}
                    </motion.button>
                  </div>

                  {hasUsername && (
                    <motion.button
                      whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}
                      onClick={handleProceed}
                      style={{
                        width: '100%', padding: '12px 14px', borderRadius: 10,
                        background: `rgba(${rgb},0.85)`,
                        border: `1px solid rgba(${rgb},0.45)`,
                        color: '#fff', fontSize: 13, fontWeight: 700,
                        cursor: 'pointer', fontFamily: "'Syne', sans-serif",
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                      }}
                    >
                      <Upload size={13} />
                      Continue to Upload
                    </motion.button>
                  )}
                </motion.div>
              )}

              {/* Done step */}
              {step === 'done' && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, paddingTop: 8 }}
                >
                  <div style={{
                    width: 64, height: 64, borderRadius: '50%',
                    background: 'rgba(34,197,94,0.15)',
                    border: '1.5px solid rgba(34,197,94,0.35)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Check size={28} color="#22c55e" strokeWidth={2.2} />
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <p style={{ fontSize: 16, fontWeight: 800, color: '#f0f0f5', margin: '0 0 4px', fontFamily: "'Syne', sans-serif" }}>
                      Profile complete!
                    </p>
                    <p style={{ fontSize: 12, color: 'rgba(240,240,245,0.5)', margin: 0, fontFamily: "'DM Mono', monospace" }}>
                      You're ready to upload content
                    </p>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.02, boxShadow: `0 0 20px rgba(${rgb},0.4)` }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleProceed}
                    style={{
                      padding: '12px 32px', borderRadius: 12,
                      background: `rgba(${rgb},0.85)`,
                      border: `1px solid rgba(${rgb},0.45)`,
                      color: '#fff', fontSize: 14, fontWeight: 700,
                      cursor: 'pointer', fontFamily: "'Syne', sans-serif",
                      display: 'flex', alignItems: 'center', gap: 8,
                    }}
                  >
                    <Upload size={15} />
                    Upload Content
                  </motion.button>
                </motion.div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </AnimatePresence>
  );
};

export default ProfileSetupGate;
